import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  ScrollView,
  Modal,
  Dimensions,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';

// Drag Indicator Icon
const DragIndicatorIcon = ({ color = '#3A3A3A', disabled = false }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path 
      d="M9.16659 14.9997C9.16659 15.9163 8.41659 16.6663 7.49992 16.6663C6.58325 16.6663 5.83325 15.9163 5.83325 14.9997C5.83325 14.083 6.58325 13.333 7.49992 13.333C8.41659 13.333 9.16659 14.083 9.16659 14.9997ZM7.49992 8.33301C6.58325 8.33301 5.83325 9.08301 5.83325 9.99967C5.83325 10.9163 6.58325 11.6663 7.49992 11.6663C8.41659 11.6663 9.16659 10.9163 9.16659 9.99967C9.16659 9.08301 8.41659 8.33301 7.49992 8.33301ZM7.49992 3.33301C6.58325 3.33301 5.83325 4.08301 5.83325 4.99967C5.83325 5.91634 6.58325 6.66634 7.49992 6.66634C8.41659 6.66634 9.16659 5.91634 9.16659 4.99967C9.16659 4.08301 8.41659 3.33301 7.49992 3.33301ZM12.4999 6.66634C13.4166 6.66634 14.1666 5.91634 14.1666 4.99967C14.1666 4.08301 13.4166 3.33301 12.4999 3.33301C11.5833 3.33301 10.8333 4.08301 10.8333 4.99967C10.8333 5.91634 11.5833 6.66634 12.4999 6.66634ZM12.4999 8.33301C11.5833 8.33301 10.8333 9.08301 10.8333 9.99967C10.8333 10.9163 11.5833 11.6663 12.4999 11.6663C13.4166 11.6663 14.1666 10.9163 14.1666 9.99967C14.1666 9.08301 13.4166 8.33301 12.4999 8.33301ZM12.4999 13.333C11.5833 13.333 10.8333 14.083 10.8333 14.9997C10.8333 15.9163 11.5833 16.6663 12.4999 16.6663C13.4166 16.6663 14.1666 15.9163 14.1666 14.9997C14.1666 14.083 13.4166 13.333 12.4999 13.333Z" 
      fill={disabled ? '#A0A0A0' : color}
    />
  </Svg>
);

// Refresh Icon for Reset All button
const RefreshIcon = ({ color = '#005CBC' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path 
      d="M14.7082 5.29134C13.4999 4.08301 11.8416 3.33301 9.99989 3.33301C6.31655 3.33301 3.34155 6.31634 3.34155 9.99967C3.34155 13.683 6.31655 16.6663 9.99989 16.6663C13.1082 16.6663 15.6999 14.5413 16.4416 11.6663H14.7082C14.0249 13.608 12.1749 14.9997 9.99989 14.9997C7.24155 14.9997 4.99989 12.758 4.99989 9.99967C4.99989 7.24134 7.24155 4.99967 9.99989 4.99967C11.3832 4.99967 12.6166 5.57467 13.5166 6.48301L10.8332 9.16634H16.6666V3.33301L14.7082 5.29134Z" 
      fill={color}
    />
  </Svg>
);

// Toggle Switch Component
interface ToggleSwitchProps {
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ value, onValueChange, disabled = false }) => (
  <TouchableOpacity
    style={styles.toggleContainer}
    onPress={() => !disabled && onValueChange(!value)}
    disabled={disabled}
  >
    <View style={[
      styles.toggleTrack,
      value ? styles.toggleTrackActive : styles.toggleTrackInactive,
      disabled && styles.toggleTrackDisabled
    ]}>
      <View style={[
        styles.toggleThumb,
        value ? styles.toggleThumbActive : styles.toggleThumbInactive
      ]} />
    </View>
  </TouchableOpacity>
);

// Day Item Interface
export interface DayItem {
  id: string;
  name: string;
  enabled: boolean;
  disabled?: boolean;
}

// Day Item Component
interface DayItemProps {
  item: DayItem;
  onToggle: (id: string, enabled: boolean) => void;
}

const DayItemComponent: React.FC<DayItemProps> = ({ item, onToggle }) => (
  <View style={styles.dayItemWrapper}>
    <View style={styles.dayItem}>
      <DragIndicatorIcon disabled={!item.enabled} />
      <View style={styles.dayContent}>
        <Text style={[
          styles.dayLabel,
          !item.enabled && styles.dayLabelDisabled
        ]}>
          {item.name}
        </Text>
      </View>
      <ToggleSwitch
        value={item.enabled}
        onValueChange={(enabled) => onToggle(item.id, enabled)}
        disabled={item.disabled}
      />
    </View>
    <View style={styles.divider} />
  </View>
);

export interface DLOFilterByDayModalProps {
  visible: boolean;
  onClose: () => void;
  onApply: (selectedDays: DayItem[]) => void;
  initialDays?: DayItem[];
}

const DLOFilterByDayModal: React.FC<DLOFilterByDayModalProps> = ({
  visible,
  onClose,
  onApply,
  initialDays = [
    { id: '0', name: 'Sunday', enabled: true, disabled: false },
    { id: '1', name: 'Monday', enabled: true, disabled: false },
    { id: '2', name: 'Tuesday', enabled: true, disabled: false },
    { id: '3', name: 'Wednesday', enabled: true, disabled: false },
    { id: '4', name: 'Thursday', enabled: true, disabled: false },
    { id: '5', name: 'Friday', enabled: true, disabled: false },
    { id: '6', name: 'Saturday', enabled: true, disabled: false },
  ],
}) => {
  const [selectedDays, setSelectedDays] = useState<DayItem[]>(initialDays);

  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;
  const isMobile = screenWidth < 768;

  const modalWidth = isMobile ? screenWidth - 32 : Math.min(380, screenWidth - 32);

  // Update selectedDays when initialDays prop changes
  useEffect(() => {
    setSelectedDays(initialDays);
  }, [initialDays]);

  // Reset selectedDays when modal becomes visible
  useEffect(() => {
    if (visible) {
      console.log('Filter by Day modal opened, setting days:', initialDays);
      setSelectedDays(initialDays);
    }
  }, [visible, initialDays]);

  const handleDayToggle = (dayId: string, enabled: boolean) => {
    setSelectedDays(prev => prev.map(day => 
      day.id === dayId ? { ...day, enabled } : day
    ));
  };

  const handleResetAll = () => {
    setSelectedDays(prev => prev.map(day => ({
      ...day,
      enabled: !day.disabled // Enable all non-disabled days
    })));
  };

  const handleApply = () => {
    onApply(selectedDays);
    onClose();
  };

  // Reset to original state on close
  const handleClose = () => {
    setSelectedDays(initialDays);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <TouchableOpacity
            style={[
              styles.modalContent,
              { 
                width: modalWidth,
                maxHeight: screenHeight - 100,
              }
            ]}
            onPress={(e) => e.stopPropagation()}
            activeOpacity={1}
          >
            {/* Title */}
            <Text style={styles.title}>Filter by Day</Text>

            <View style={styles.container}>
              {/* Day List */}
              <View style={styles.listContainer}>
                <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                  {selectedDays.map((day) => (
                    <DayItemComponent
                      key={day.id}
                      item={day}
                      onToggle={handleDayToggle}
                    />
                  ))}
                </ScrollView>
              </View>

              {/* Custom Sticky Footer */}
              <View style={styles.stickyFooter}>
                <View style={styles.buttonGroup}>
                  {/* Reset All Button */}
                  <TouchableOpacity style={styles.resetButton} onPress={handleResetAll}>
                    <RefreshIcon />
                    <Text style={styles.resetButtonText}>Reset all</Text>
                  </TouchableOpacity>

                  {/* Cancel and Apply Buttons */}
                  <View style={styles.buttonHierarchy}>
                    <TouchableOpacity style={styles.cancelButton} onPress={handleClose}>
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.applyButton} onPress={handleApply}>
                      <Text style={styles.applyButtonText}>Apply</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalContent: {
    backgroundColor: '#FBFCFE',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    shadowColor: 'rgba(58, 58, 58, 0.08)',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 8,
    minWidth: 320,
    maxWidth: 460,
    position: 'relative',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 24px 0 rgba(58, 58, 58, 0.08)',
      },
    }),
  },
  title: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 28,
    color: '#000',
    marginTop: 12,
    marginHorizontal: 20,
    marginBottom: 0,
  },
  modalCustomStyle: {
    paddingTop: '2%',
    paddingRight: 0,
    paddingBottom: 60, // Extra space for sticky footer
    paddingLeft: 0,
  },
  container: {
    width: '100%',
    minHeight: 400,
    flex: 1,
  },
  listContainer: {
    flex: 1,
    marginBottom: 60, // Space for sticky footer
    marginTop: 16,
  },
  scrollView: {
    flex: 1,
    maxHeight: 350,
  },
  dayItemWrapper: {
    paddingHorizontal: 16,
  },
  dayItem: {
    flexDirection: 'row',
    minHeight: 48,
    paddingVertical: 8,
    alignItems: 'center',
    gap: 8,
  },
  dayContent: {
    flex: 1,
    paddingRight: 8,
    gap: 2,
  },
  dayLabel: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    color: '#3A3A3A',
  },
  dayLabelDisabled: {
    color: '#A0A0A0',
  },
  divider: {
    height: 1,
    backgroundColor: '#D7E5F8',
    marginVertical: 0,
  },
  toggleContainer: {
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  toggleTrack: {
    width: 32,
    height: 16,
    padding: 3,
    borderRadius: 24,
    justifyContent: 'center',
  },
  toggleTrackActive: {
    backgroundColor: '#005CBC',
    alignItems: 'flex-end',
  },
  toggleTrackInactive: {
    backgroundColor: '#616161',
    alignItems: 'flex-start',
  },
  toggleTrackDisabled: {
    backgroundColor: '#616161',
  },
  toggleThumb: {
    width: 10,
    height: 10,
    borderRadius: 999,
    backgroundColor: '#FFF',
  },
  toggleThumbActive: {
    // Additional styling if needed
  },
  toggleThumbInactive: {
    // Additional styling if needed
  },
  stickyFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
    paddingVertical: 16,
    paddingHorizontal: 12,
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 8,
    ...Platform.select({
      web: {
        boxShadow: '0 0 24px 0 rgba(0, 92, 188, 0.08)',
      },
    }),
  },
  buttonGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
  },
  resetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    height: 32,
    paddingVertical: 4,
    paddingHorizontal: 0,
  },
  resetButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    color: '#005CBC',
    maxWidth: 150,
  },
  buttonHierarchy: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
  },
  cancelButton: {
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 4,
  },
  cancelButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    color: '#005CBC',
    maxWidth: 150,
  },
  applyButton: {
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 24,
    backgroundColor: '#005CBC',
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
  applyButtonText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    color: '#FFF',
    maxWidth: 150,
  },
});

export default DLOFilterByDayModal;
